import { useRef } from 'react';
import { Pencil } from 'lucide-react';
import Spinner from '../ui/Spinner.js';
import { useTranslation } from 'react-i18next';

interface RateCardsSectionProps {
  referenceRate: number;
  actualRate: string;
  onActualRateChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  spread: number;
  rateError?: string;
  rateSource?: string;
  rateFetchedAt?: Date | null;
  isLoadingRate?: boolean;
  /** Binance raw price (USDT per 1 crypto) and crypto symbol for "1 BTC = 71,736 USDT" */
  binancePriceUsdt?: number | null;
  binanceCryptoSymbol?: string;
  /** Currency symbols for display */
  cryptoSymbol?: string;
  fiatSymbol?: string;
  /** Oracle equivalent of the total crypto payout */
  oracleEquivalent?: { label: string; cryptoTotal: string; fiatEquivalent: string; currency: string };
  /** When true, actual rate is display-only (auto-computed from amounts) */
  readOnly?: boolean;
  /** When true, display as "1 fiat = X crypto" instead of "1 crypto = X fiat" (for BUY mode) */
  invertedDisplay?: boolean;
  /** Absolute profit: oracle_amount - actual_amount */
  profit?: { value: number; currency: string };
}

export function RateCardsSection({
  referenceRate,
  actualRate,
  onActualRateChange,
  spread,
  rateError,
  isLoadingRate,
  rateSource,
  cryptoSymbol = 'BTC',
  fiatSymbol = '€',
  oracleEquivalent,
  readOnly = false,
  invertedDisplay = false,
  profit,
}: RateCardsSectionProps) {
  const rateInputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();

  const handleEditClick = () => {
    rateInputRef.current?.focus();
    rateInputRef.current?.select();
  };
  const getSpreadClass = () => {
    if (spread > 0.005) return 'text-ctp-green';
    if (spread < -0.005) return 'text-ctp-red';
    return 'text-foreground';
  };

  const formatSpread = () => {
    if (Math.abs(spread) < 0.005) return '0.00';
    return spread > 0 ? `+${spread.toFixed(2)}` : spread.toFixed(2);
  };

  const formatRateDisplay = (rate: number | string) => {
    const num = typeof rate === 'string' ? parseFloat(rate) : rate;
    if (isNaN(num)) return '0.0000';
    return num.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 });
  };

  return (
    <div className="mt-6 p-5 bg-surface-hover/50 rounded-xl border border-border">
      {/* Header */}
      <h3 className="text-base font-semibold text-foreground mb-5">
        {t('cashier.createOperation.unifiedRate', 'Unified Rate')}
      </h3>

      {/* Rate Grid - Equal columns with centered content */}
      <div className="grid grid-cols-3 max-sm:grid-cols-1 gap-4">
        {/* Reference Rate */}
        <div className="text-center sm:text-left">
          <div className="text-xs font-medium text-foreground-muted mb-2">
            {t('cashier.createOperation.referenceRate', 'Reference Rate')}
          </div>
          {isLoadingRate ? (
            <div className="flex items-center justify-center sm:justify-start gap-2">
              <Spinner size="sm" />
              <span className="text-sm text-foreground-muted">
                {t('cashier.createOperation.loading', 'Loading...')}
              </span>
            </div>
          ) : (
            <>
              <div className="text-sm text-foreground font-medium">
                {invertedDisplay
                  ? `1 ${fiatSymbol} = ${formatRateDisplay(referenceRate > 0 ? 1 / referenceRate : 0)} ${cryptoSymbol}`
                  : `1 ${cryptoSymbol} = ${fiatSymbol}${formatRateDisplay(referenceRate)}`
                }
              </div>
              {rateSource && (
                <div className="text-[10px] text-foreground-muted mt-0.5 opacity-70">
                  {rateSource}
                </div>
              )}
            </>
          )}
        </div>

        {/* Actual Rate (editable or computed) */}
        <div className="text-center">
          <div className="text-xs font-medium text-foreground-muted mb-2">
            {t('cashier.createOperation.actualRateEditable', readOnly ? 'Actual Rate (computed)' : 'Actual Rate (editable)')}
          </div>
          <div className="flex items-center justify-center gap-1.5 flex-wrap">
            {invertedDisplay ? (
              <span className="text-sm text-foreground">1 {fiatSymbol} =</span>
            ) : (
              <span className="text-sm text-foreground">1 {cryptoSymbol} = {fiatSymbol}</span>
            )}
            {readOnly ? (
              <span className="w-[90px] py-1.5 px-2 text-sm font-semibold text-center text-foreground font-mono">
                {invertedDisplay
                  ? formatRateDisplay(parseFloat(actualRate) > 0 ? 1 / parseFloat(actualRate) : 0)
                  : formatRateDisplay(actualRate)
                }
              </span>
            ) : (
              <input
                ref={rateInputRef}
                type="text"
                inputMode="decimal"
                className="
                  w-[90px] py-1.5 px-2 text-sm font-medium text-center
                  border border-border rounded-md
                  bg-surface text-foreground
                  focus:outline-none focus:border-ctp-blue focus:ring-1 focus:ring-ctp-blue/20
                "
                value={actualRate}
                onChange={onActualRateChange}
              />
            )}
            {!readOnly && (
              <button
                type="button"
                onClick={handleEditClick}
                className="text-ctp-blue flex-shrink-0 hover:text-ctp-blue/80 transition-colors p-1 -m-1"
                title={t('cashier.createOperation.editRate', 'Edit rate')}
              >
                <Pencil size={14} />
              </button>
            )}
          </div>
          {rateError && (
            <span className="text-xs text-ctp-red mt-1.5 block">{rateError}</span>
          )}
        </div>

        {/* Spread */}
        <div className="text-center sm:text-right">
          <div className="text-xs font-medium text-foreground-muted mb-2">
            {t('cashier.createOperation.spread', 'Spread')}
          </div>
          <div className={`text-sm font-medium ${getSpreadClass()}`}>
            {formatSpread()}%
          </div>
          {profit !== undefined && (
            <div className={`text-xs font-semibold mt-0.5 font-mono ${
              profit.value > 0 ? 'text-ctp-green' : profit.value < 0 ? 'text-ctp-red' : 'text-foreground-muted'
            }`}>
              {profit.value > 0 ? '+' : ''}{profit.value.toFixed(
                ['BTC','ETH','BNB','SOL','XRP'].includes(profit.currency.toUpperCase()) ? 6 : 2
              )} {profit.currency}
            </div>
          )}
        </div>
      </div>

      {/* Oracle equivalent total */}
      {oracleEquivalent && (
        <div className="mt-4 pt-4 border-t border-border flex items-center justify-between">
          <span className="text-xs font-medium text-foreground-muted uppercase tracking-wide">
            {oracleEquivalent.label}
          </span>
          <span className="text-sm font-semibold font-mono text-ctp-blue">
            {oracleEquivalent.cryptoTotal} → {oracleEquivalent.fiatEquivalent} {oracleEquivalent.currency}
          </span>
        </div>
      )}
    </div>
  );
}
